// Skippable, speed-scaled waits and tweens for the EventPlayer. Every juice effect goes through
// here so the global speed factor and "skip all" work uniformly (docs/08 §6).
import gsap from 'gsap';

type Waiter = { resolve: () => void; timer: ReturnType<typeof setTimeout> };

export class Clock {
  speed = 1;
  private skipping = false;
  private waiters = new Set<Waiter>();
  private tweens = new Set<gsap.core.Tween>();

  get isSkipping(): boolean {
    return this.skipping;
  }

  /** Scaled duration in ms (0 while skipping). */
  ms(ms: number): number {
    return this.skipping ? 0 : ms / this.speed;
  }

  wait(ms: number): Promise<void> {
    const d = this.ms(ms);
    if (d <= 0) return Promise.resolve();
    return new Promise((resolve) => {
      const w: Waiter = {
        resolve: () => {
          clearTimeout(w.timer);
          this.waiters.delete(w);
          resolve();
        },
        timer: setTimeout(() => w.resolve(), d),
      };
      this.waiters.add(w);
    });
  }

  /** gsap.to with duration in ms, scaled by speed; resolves on completion or skip. */
  tween(target: gsap.TweenTarget, ms: number, vars: gsap.TweenVars): Promise<void> {
    const d = this.ms(ms);
    if (d <= 0) {
      gsap.set(target, stripTweenOnly(vars));
      return Promise.resolve();
    }
    return new Promise((resolve) => {
      const tw = gsap.to(target, {
        ...vars,
        duration: d / 1000,
        onComplete: () => {
          this.tweens.delete(tw);
          resolve();
        },
      });
      this.tweens.add(tw);
    });
  }

  /** Fire-and-forget tween (still skippable). */
  fx(target: gsap.TweenTarget, ms: number, vars: gsap.TweenVars): void {
    void this.tween(target, ms, vars);
  }

  /** Finish everything in flight immediately. Stays in skip mode until `endSkip`. */
  skip(): void {
    this.skipping = true;
    for (const tw of [...this.tweens]) tw.progress(1);
    this.tweens.clear();
    for (const w of [...this.waiters]) w.resolve();
  }

  endSkip(): void {
    this.skipping = false;
  }
}

function stripTweenOnly(vars: gsap.TweenVars): gsap.TweenVars {
  const { ease: _e, yoyo: _y, repeat: _r, onComplete: _c, delay: _d, ...rest } = vars;
  if (_y && _r) return {};
  return rest;
}

export const clock = new Clock();
